import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, FileUp, CheckCircle, XCircle, Clock,
  Rocket, Pause, Play, Archive, Send, Eye, AlertTriangle,
  ClipboardCheck, Users, MapPin, Database, Settings, Shield,
} from 'lucide-react';
import api from '../../lib/api';

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  review_pending: { label: 'Review Pending', color: 'bg-amber-100 text-amber-700' },
  pilot_ready: { label: 'Pilot Ready', color: 'bg-emerald-100 text-emerald-700' },
  pilot_activated: { label: 'Pilot Activated', color: 'bg-green-100 text-green-800' },
  pilot_live: { label: 'Pilot Live', color: 'bg-green-200 text-green-900' },
  pilot_paused: { label: 'Paused', color: 'bg-orange-100 text-orange-700' },
  completed: { label: 'Completed', color: 'bg-teal-100 text-teal-700' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500' },
};

const TYPE_MAP = { pilot: 'Pilot Rollout', go_live: 'Go-Live Setup', data_migration: 'Data Migration', implementation: 'Implementation' };

export default function OnboardingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [detailRes, eventsRes, readinessRes] = await Promise.all([
        api.get(`/client-onboarding/${id}`),
        api.get(`/client-onboarding/${id}/events`).catch(() => ({ data: [] })),
        api.get(`/client-onboarding/${id}/readiness`).catch(() => ({ data: null })),
      ]);
      setData(detailRes.data);
      setEvents(eventsRes.data || []);
      setReadiness(readinessRes.data);
    } catch (e) {
      toast.error('Failed to load onboarding');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [confirmDialog, setConfirmDialog] = useState(null);

  const handleAction = async (action, label) => {
    setConfirmDialog({ action, label });
  };

  const executeAction = async () => {
    if (!confirmDialog) return;
    const { action, label } = confirmDialog;
    setConfirmDialog(null);
    setActionLoading(action);
    try {
      await api.post(`/client-onboarding/${id}/${action}`);
      toast.success(`${label} successful`);
      fetchData();
    } catch (e) {
      const err = e.response?.data?.detail?.error || e.response?.data?.error;
      toast.error(err?.message || `Failed to ${label}`);
      if (err?.details?.blockingIssues) {
        err.details.blockingIssues.forEach(b => toast.error(b, { duration: 5000 }));
      }
    }
    setActionLoading('');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Onboarding not found</div>;

  const sCfg = STATUS_MAP[data.status] || { label: data.status, color: 'bg-gray-100' };
  const progress = data.progress || { percent: 0, stepCompletion: {} };
  const checklist = data.checklist || {};
  const checklistDone = Object.values(checklist).filter(Boolean).length;
  const checklistTotal = Object.keys(checklist).length;

  return (
    <div className="space-y-6" data-testid="onboarding-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/onboarding')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{data.onboardingName}</h1>
              <Badge variant="outline" className={`${sCfg.color} text-xs`}>{sCfg.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{data.onboardingCode} | {TYPE_MAP[data.type] || data.type}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['draft', 'in_progress'].includes(data.status) && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/onboarding/${id}/import`)} data-testid="import-btn">
              <FileUp className="h-4 w-4 mr-1" />Import Data
            </Button>
          )}
          {data.status === 'in_progress' && (
            <Button size="sm" onClick={() => handleAction('submit-review', 'Submit for Review')} disabled={!!actionLoading} data-testid="submit-review-btn">
              {actionLoading === 'submit-review' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Submit for Review
            </Button>
          )}
          {data.status === 'review_pending' && (
            <Button size="sm" onClick={() => handleAction('review', 'Approve Review')} disabled={!!actionLoading} data-testid="approve-review-btn">
              {actionLoading === 'review' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Approve Review
            </Button>
          )}
          {data.status === 'pilot_ready' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAction('pilot/activate', 'Activate Pilot')} disabled={!!actionLoading} data-testid="activate-pilot-btn">
              {actionLoading === 'pilot/activate' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
              Activate Pilot
            </Button>
          )}
          {['pilot_activated', 'pilot_live'].includes(data.status) && (
            <Button size="sm" variant="outline" className="text-orange-600 border-orange-300" onClick={() => handleAction('pilot/pause', 'Pause Pilot')} disabled={!!actionLoading} data-testid="pause-pilot-btn">
              <Pause className="h-4 w-4 mr-1" />Pause
            </Button>
          )}
          {data.status === 'pilot_paused' && (
            <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => handleAction('pilot/resume', 'Resume Pilot')} disabled={!!actionLoading} data-testid="resume-pilot-btn">
              <Play className="h-4 w-4 mr-1" />Resume
            </Button>
          )}
          {data.status === 'pilot_live' && (
            <Button size="sm" onClick={() => handleAction('complete', 'Complete Onboarding')} disabled={!!actionLoading} data-testid="complete-btn">
              <CheckCircle className="h-4 w-4 mr-1" />Complete
            </Button>
          )}
          {!['completed', 'archived'].includes(data.status) && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => handleAction('archive', 'Archive')} disabled={!!actionLoading} data-testid="archive-btn">
              <Archive className="h-4 w-4 mr-1" />Archive
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/onboarding/${id}/review`)} data-testid="review-page-btn">
            <Eye className="h-4 w-4 mr-1" />Readiness
          </Button>
        </div>
      </div>

      {/* Progress & KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Wizard Progress</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
              </div>
              <span className="text-sm font-bold">{progress.percent}%</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Checklist</p>
            <p className="text-lg font-bold">{checklistDone}/{checklistTotal}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Target Go-Live</p>
            <p className="text-sm font-medium">{data.dates?.targetGoLiveDate ? new Date(data.dates.targetGoLiveDate).toLocaleDateString() : 'Not set'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Pilot Stage</p>
            <p className="text-sm font-medium">{data.pilotConfig?.targetPilotStage || 'Not configured'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Readiness Alert */}
      {readiness && !readiness.eligible && readiness.blockingIssues?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Blocking Issues ({readiness.blockingIssues.length})</p>
                <ul className="mt-1 space-y-0.5">
                  {readiness.blockingIssues.map((b, i) => (
                    <li key={i} className="text-xs text-amber-700">- {b}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DetailSection title="Overview" icon={ClipboardCheck}>
          <InfoRow label="Description" value={data.description || '-'} />
          <InfoRow label="Type" value={TYPE_MAP[data.type] || data.type} />
          <InfoRow label="Start Date" value={data.dates?.targetStartDate || '-'} />
          <InfoRow label="Go-Live Date" value={data.dates?.targetGoLiveDate || '-'} />
          <InfoRow label="Created" value={data.audit?.createdAt ? new Date(data.audit.createdAt).toLocaleString() : '-'} />
        </DetailSection>

        <DetailSection title="Scope" icon={MapPin}>
          <InfoRow label="States" value={data.scope?.states?.join(', ') || '-'} />
          <InfoRow label="Branches" value={data.scope?.branches?.join(', ') || '-'} />
          <InfoRow label="Services" value={data.scope?.services?.join(', ') || '-'} />
          <InfoRow label="Modules" value={data.scope?.modules?.join(', ') || '-'} />
          <InfoRow label="Notes" value={data.scope?.notes || '-'} />
        </DetailSection>

        <DetailSection title="Ownership" icon={Users}>
          <InfoRow label="Implementation Owner" value={data.ownership?.implementationOwnerUserId || 'Not assigned'} />
          <InfoRow label="Reviewer" value={data.ownership?.reviewerUserId || 'Not assigned'} />
          <InfoRow label="Activation Approver" value={data.ownership?.activationApproverUserId || 'Not assigned'} />
        </DetailSection>

        <DetailSection title="Migration Config" icon={Database}>
          <InfoRow label="Source System" value={data.migrationConfig?.sourceSystem || '-'} />
          <InfoRow label="Datasets" value={data.migrationConfig?.datasets?.join(', ') || '-'} />
          <InfoRow label="Mapping Notes" value={data.migrationConfig?.mappingNotes || '-'} />
        </DetailSection>

        <DetailSection title="Pilot Configuration" icon={Rocket}>
          <InfoRow label="Pilot Enabled" value={data.pilotConfig?.pilotEnabled ? 'Yes' : 'No'} />
          <InfoRow label="Target Stage" value={data.pilotConfig?.targetPilotStage || '-'} />
          <InfoRow label="Feature Flags" value={data.pilotConfig?.featureFlagsRequested?.join(', ') || '-'} />
          <InfoRow label="Rollback Notes" value={data.pilotConfig?.rollbackNotes || '-'} />
        </DetailSection>

        <DetailSection title="Checklist" icon={Shield}>
          {Object.entries(checklist).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 py-1">
              {v ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-gray-300" />}
              <span className="text-sm capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
            </div>
          ))}
        </DetailSection>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" />Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No events yet</p>
          ) : (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {events.map((evt, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{evt.eventLabel}</p>
                    <p className="text-xs text-muted-foreground">
                      {evt.actor?.name || 'System'} | {evt.createdAt ? new Date(evt.createdAt).toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={open => !open && setConfirmDialog(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm: {confirmDialog?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.action?.includes('pilot/activate')
                ? 'This will activate the pilot rollout using Day 24 controls. Feature flags and kill switches will be adjusted based on the target stage.'
                : confirmDialog?.action?.includes('pilot/pause')
                ? 'This will pause the active pilot. No rollout changes will be reverted until resumed.'
                : confirmDialog?.action?.includes('archive')
                ? 'This will archive the onboarding project. It can be found under archived filters.'
                : `Are you sure you want to ${confirmDialog?.label?.toLowerCase()}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeAction} data-testid="confirm-proceed">
              {confirmDialog?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DetailSection({ title, icon: Icon, children }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">{children}</CardContent>
    </Card>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}
